import { usePlanner } from "@/features/space-designer/lib/store";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fileToBlueprint } from "@/features/space-designer/lib/blueprint-loader";
import { detectWallsFromImage } from "@/features/space-designer/lib/wall-detector";
import { useRef, useState, useEffect } from "react";
import { Upload, Lock, Unlock, Trash2, ImageIcon, Wand2, Layers as LayersIcon, Check, X } from "lucide-react";
import { Copy } from "lucide-react";
import type { Wall, WallMaterial } from "@/features/space-designer/lib/types";
import { Palette, Save } from "lucide-react";
import { useSvgLibrary } from "@/features/space-designer/lib/svg-library";
import { toast } from "sonner";
import { findMergeCandidate, canMerge } from "@/features/space-designer/lib/merge";
import { isWorkstationShape } from "@/features/space-designer/lib/workstation-layout";
import { isAllocatable, seatCodeFor } from "@/features/space-designer/lib/allocation";
import { Users } from "lucide-react";

export function Inspector() {
  const {
    scene, selectedIds, updateItem, setBlueprint, updateBlueprint,
    tool, wallDraft, setWallDraft, updateWall,
    layers, setLayers, snapSettings, setSnapSettings, gridSize, setGridSize,
    wallCandidates, setWallCandidates, acceptWallCandidates,
    explodeItem, mergeSelection, ungroupSelected,
    groupSelection,
    duplicateSelected, removeSelected,
    saveClusterPreset, clearClusterPreset,
  } = usePlanner();
  const saveSelectionAsRoom = usePlanner((s) => s.saveSelectionAsRoom);
  const addTeam = usePlanner((s) => s.addTeam);
  const updateTeam = usePlanner((s) => s.updateTeam);
  const removeTeam = usePlanner((s) => s.removeTeam);
  const teams = scene.teams ?? [];
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamColor, setNewTeamColor] = useState("#3b82f6");
  const [newRoomName, setNewRoomName] = useState("");
  const selected = scene.items.filter((it) => selectedIds.includes(it.id));
  const single = selected.length === 1 ? selected[0] : null;
  // A wall is a selectable object too (not an item). Surface it so the header +
  // the Wall card both react to a selected wall.
  const selectedWall = selectedIds.length === 1 ? scene.walls.find((w) => w.id === selectedIds[0]) : undefined;
  const mergeCand = findMergeCandidate(selected, scene.items);
  const groupSelected = selected.some((s) => s.groupId);
  const uniqueGroupIds = new Set(selected.map((s) => s.groupId).filter(Boolean) as string[]);
  const allInSameGroup =
    selected.length > 1 &&
    uniqueGroupIds.size === 1 &&
    selected.every((s) => s.groupId);
  const canGroupSelection = selected.length > 1 && !allInSameGroup;
  const addLocalAsset = useSvgLibrary((s) => s.addAsset);
  const bpFileRef = useRef<HTMLInputElement>(null);
  const [bpLoading, setBpLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [sensitivity, setSensitivity] = useState(60);
  const [mounted, setMounted] = useState(false);
  const candidates = wallCandidates;

  useEffect(() => setMounted(true), []);

  async function onBpUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      setBpLoading(true);
      const bp = await fileToBlueprint(f);
      setBlueprint(bp);
    } catch (err) {
      console.error(err);
      alert("Could not load blueprint. Try a PNG, JPG, or PDF.");
    } finally {
      setBpLoading(false);
    }
  }

  async function runDetect() {
    if (!scene.blueprint) return;
    setDetecting(true);
    try {
      const threshold = Math.round(60 + (100 - sensitivity) * 1.2); // higher sensitivity -> lower threshold OK; map sensibly
      const minLengthRatio = 0.04 + (100 - sensitivity) / 100 * 0.12;
      const walls = await detectWallsFromImage(
        scene.blueprint.src,
        { x: scene.blueprint.x, y: scene.blueprint.y, w: scene.blueprint.w, h: scene.blueprint.h },
        { threshold, minLengthRatio },
      );
      setWallCandidates(walls);
    } catch (err) {
      console.error(err);
      alert("Wall detection failed.");
    } finally {
      setDetecting(false);
    }
  }

  function acceptCandidates() {
    acceptWallCandidates();
  }

  return (
    <aside className="w-72 shrink-0 border-l border-border bg-card flex flex-col h-full">
      <div className="p-3 border-b border-border">
        <div className="text-sm font-semibold">Inspector</div>
        <div className="text-xs text-muted-foreground">
          {selectedWall ? "Wall selected" : selected.length === 0 ? "Nothing selected" : selected.length === 1 ? single?.label : `${selected.length} items`}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Layers & Snapping */}
        <div className="rounded-md border border-border p-3 space-y-2 bg-background/40">
          <div className="text-xs font-semibold flex items-center gap-1.5"><LayersIcon className="w-3.5 h-3.5" /> Layers</div>
          <div className="space-y-1.5">
            {(["blueprint","rooms","walls","items","measurements","areas","grid"] as const).map((k) => (
              <label key={k} className="flex items-center justify-between gap-2 text-xs capitalize cursor-pointer">
                <span className="text-foreground/80 truncate">{k}</span>
                <Switch checked={layers[k]} onCheckedChange={(v) => setLayers({ [k]: v })} className="shrink-0" />
              </label>
            ))}
          </div>
          <div className="text-xs font-semibold pt-2">Snapping</div>
          <div className="space-y-1.5">
            <label className="flex items-center justify-between gap-2 text-xs cursor-pointer">
              <span className="text-foreground/80">Snap to grid</span>
              <Switch checked={snapSettings.grid} onCheckedChange={(v) => setSnapSettings({ grid: v })} />
            </label>
            <label className="flex items-center justify-between gap-2 text-xs cursor-pointer">
              <span className="text-foreground/80">Angle snap (15° · or hold Shift)</span>
              <Switch checked={snapSettings.angle} onCheckedChange={(v) => setSnapSettings({ angle: v })} />
            </label>
            <label className="flex items-center justify-between gap-2 text-xs cursor-pointer">
              <span className="text-foreground/80">Snap to wall ends</span>
              <Switch checked={snapSettings.vertex} onCheckedChange={(v) => setSnapSettings({ vertex: v })} />
            </label>
          </div>
          <div className="pt-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Grid size: {gridSize} cm</Label>
            <Slider value={[gridSize]} min={5} max={100} step={5} onValueChange={(v) => setGridSize(v[0])} />
          </div>
        </div>

        {/* Wall config — type + thickness for the wall tool / a selected wall */}
        {(() => {
          const selWall = selectedWall;
          if (!selWall && tool !== "wall") return null;
          const material: WallMaterial = selWall?.material ?? wallDraft.material;
          const thickness = selWall?.thickness ?? wallDraft.thickness;
          const setMaterial = (m: WallMaterial) => (selWall ? updateWall(selWall.id, { material: m }) : setWallDraft({ material: m }));
          const setThickness = (t: number) => (selWall ? updateWall(selWall.id, { thickness: t }) : setWallDraft({ thickness: t }));
          return (
            <div className="rounded-md border border-border p-3 space-y-2 bg-background/40">
              <div className="text-xs font-semibold">{selWall ? "Wall" : "New wall"}</div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Type</Label>
                <Select value={material} onValueChange={(v) => setMaterial(v as WallMaterial)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cemented">Cemented</SelectItem>
                    <SelectItem value="gypsum">Gypsum</SelectItem>
                    <SelectItem value="glass">Glass</SelectItem>
                    <SelectItem value="wooden">Wooden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Thickness: {thickness} cm</Label>
                <Slider value={[thickness]} min={2} max={30} step={1} onValueChange={(v) => setThickness(v[0])} />
              </div>
              {!selWall && (
                <p className="text-[10px] text-muted-foreground leading-snug">
                  Applies to walls you draw. Hold <b>Shift</b> while dragging for a straight line.
                </p>
              )}
            </div>
          );
        })()}

        {/* Teams / departments */}
        <div className="rounded-md border border-border p-3 space-y-2 bg-background/40">
          <div className="text-xs font-semibold flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Teams</div>
          <p className="text-[11px] text-muted-foreground leading-snug">Color-coded departments. Assign a cabin or seat to a team in its allocation panel.</p>
          <div className="space-y-1.5">
            {teams.length === 0 && (
              <div className="text-[11px] text-muted-foreground italic">No teams yet.</div>
            )}
            {teams.map((t) => (
              <div key={t.id} className="flex items-center gap-2">
                <input
                  type="color"
                  value={t.color}
                  onChange={(e) => updateTeam(t.id, { color: e.target.value })}
                  className="h-7 w-9 rounded border border-border bg-background"
                />
                <Input
                  value={t.name}
                  onChange={(e) => updateTeam(t.id, { name: e.target.value })}
                  className="h-7 text-xs flex-1"
                />
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => removeTeam(t.id)} title="Remove team">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input
              type="color"
              value={newTeamColor}
              onChange={(e) => setNewTeamColor(e.target.value)}
              className="h-7 w-9 rounded border border-border bg-background"
            />
            <Input
              value={newTeamName}
              placeholder="Team name (e.g. Design)"
              onChange={(e) => setNewTeamName(e.target.value)}
              className="h-7 text-xs flex-1"
            />
            <Button
              size="sm"
              className="h-7"
              onClick={() => {
                if (!newTeamName.trim()) { toast.error("Name the team first"); return; }
                addTeam(newTeamName, newTeamColor);
                setNewTeamName("");
              }}
            >Add</Button>
          </div>
        </div>

        {/* Blueprint underlay panel */}
        <div className="rounded-md border border-border p-3 space-y-3 bg-background/40">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Blueprint underlay</div>
            {mounted && scene.blueprint && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => setBlueprint(null)} title="Remove blueprint">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          <input ref={bpFileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={onBpUpload} />
          <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={() => bpFileRef.current?.click()} disabled={bpLoading}>
            <Upload className="w-3.5 h-3.5" />
            {bpLoading ? "Loading…" : mounted && scene.blueprint ? "Replace blueprint" : "Upload PNG / PDF"}
          </Button>
          {mounted && scene.blueprint && (
            <>
              <div className="text-[11px] text-muted-foreground truncate">{scene.blueprint.name}</div>
              <Field label={`Opacity: ${Math.round(scene.blueprint.opacity * 100)}%`}>
                <Slider value={[scene.blueprint.opacity * 100]} min={5} max={100} step={1} onValueChange={(v) => updateBlueprint({ opacity: v[0] / 100 })} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Width (cm)">
                  <Input type="number" value={Math.round(scene.blueprint.w)} className="h-8" onChange={(e) => {
                    const w = Math.max(50, Number(e.target.value) || 0);
                    const ratio = scene.blueprint!.h / scene.blueprint!.w;
                    updateBlueprint({ w, h: w * ratio });
                  }} />
                </Field>
                <Field label={`Rotation: ${Math.round(scene.blueprint.rotation)}°`}>
                  <Slider value={[scene.blueprint.rotation]} min={0} max={360} step={1} onValueChange={(v) => updateBlueprint({ rotation: v[0] })} />
                </Field>
              </div>
              <Button size="sm" variant={scene.blueprint.locked ? "default" : "outline"} className="w-full gap-1.5" onClick={() => updateBlueprint({ locked: !scene.blueprint!.locked })}>
                {scene.blueprint.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                {scene.blueprint.locked ? "Locked (click to unlock)" : "Lock position"}
              </Button>

              <div className="border-t border-border pt-2 space-y-2">
                <div className="text-xs font-semibold flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" /> Auto-detect walls</div>
                <Field label={`Sensitivity: ${sensitivity}`}>
                  <Slider value={[sensitivity]} min={20} max={95} step={5} onValueChange={(v) => setSensitivity(v[0])} />
                </Field>
                <Button size="sm" variant="secondary" className="w-full gap-1.5" disabled={detecting} onClick={runDetect}>
                  <Wand2 className="w-3.5 h-3.5" />
                  {detecting ? "Analyzing…" : candidates ? "Re-run detection" : "Detect walls"}
                </Button>
                {candidates && (
                  <div className="rounded border border-measure-color/40 bg-background/60 p-2 space-y-2">
                    <div className="text-xs">Found <b>{candidates.length}</b> wall candidates. Review on canvas in pink — accept to add as real walls.</div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="default" className="flex-1 h-7 gap-1" onClick={acceptCandidates}>
                        <Check className="w-3.5 h-3.5" /> Accept all
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 h-7 gap-1" onClick={() => setWallCandidates(null)}>
                        <X className="w-3.5 h-3.5" /> Discard
                      </Button>
                    </div>
                    <div className="text-[10px] text-muted-foreground">After accepting, drag wall endpoints isn't supported — but you can double-click to delete and redraw, or nudge with the Wall tool.</div>
                  </div>
                )}
              </div>

              <div className="text-[11px] text-muted-foreground leading-relaxed">
                Tip: scale the blueprint so a known wall matches the Measure tool, then lock it and trace rooms/walls on top.
              </div>
            </>
          )}
        </div>

        {!single && (
          <div className="text-xs text-muted-foreground leading-relaxed">
            Select an item on the canvas to edit it. Tips:
            <ul className="list-disc ml-4 mt-2 space-y-1">
              <li>Drag corners to resize</li>
              <li>Drag top handle to rotate</li>
              <li>Shift-click to multi-select</li>
              <li>Double-click a wall or measurement to delete</li>
              <li>Hold Alt + drag to pan</li>
              <li>Ctrl/Cmd + scroll to zoom</li>
            </ul>
          </div>
        )}
        {!single && canGroupSelection && !mergeCand && (
          <div className="rounded-md border bg-muted/40 p-2 space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Selection · {selected.length} items
            </div>
            <div className="text-xs text-muted-foreground">
              {groupSelected
                ? "Some items belong to other groups. Grouping will rebind them all into one new cluster."
                : "Group these into a single custom cluster — click any part to select the whole group, drag to move together, duplicate as one unit."}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={groupSelection}>Group selection</Button>
              {groupSelected && (
                <Button size="sm" variant="outline" onClick={ungroupSelected}>
                  Ungroup first
                </Button>
              )}
            </div>
          </div>
        )}
        {!single && selected.length >= 2 && (
          <div className="rounded-md border bg-muted/40 p-2 space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Save as room preset
            </div>
            <div className="text-xs text-muted-foreground">
              Saves the selected components (with their relative positions) into the Library under <b>My Rooms</b>. Drag from there to re-drop anywhere.
            </div>
            <div className="flex gap-2">
              <Input
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="Room name (e.g. Workstation 6-pack)"
                className="h-8 text-xs"
              />
              <Button
                size="sm"
                onClick={() => {
                  const r = saveSelectionAsRoom(newRoomName || "Custom Room");
                  if (r) {
                    toast.success(`Saved “${r.name}” to My Rooms (${r.children.length} parts).`);
                    setNewRoomName("");
                  } else {
                    toast.error("Nothing to save.");
                  }
                }}
              >
                <Save className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
            </div>
          </div>
        )}
        {!single && selected.length > 1 && (mergeCand || (groupSelected && !canGroupSelection)) && (
          <div className="rounded-md border bg-muted/40 p-2 space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Workstation group
            </div>
            {mergeCand && (
              <div className="text-xs text-muted-foreground">
                {canMerge(mergeCand)
                  ? `Ready to merge into ${mergeCand.desk.origin?.label}.`
                  : mergeCand.reason}
              </div>
            )}
            <div className="flex gap-2">
              {mergeCand && (
                <Button size="sm" disabled={!canMerge(mergeCand)} onClick={mergeSelection}>
                  Merge back
                </Button>
              )}
              {groupSelected && (
                <Button size="sm" variant="outline" onClick={ungroupSelected}>
                  Ungroup
                </Button>
              )}
            </div>
          </div>
        )}
        {single && (
          <>
            <Field label="Label">
              <Input value={single.label} onChange={(e) => updateItem(single.id, { label: e.target.value })} className="h-8" />
            </Field>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 h-8 gap-1.5" onClick={duplicateSelected}>
                <Copy className="w-3.5 h-3.5" /> Duplicate
              </Button>
              <Button size="sm" variant="outline" className="flex-1 h-8 gap-1.5 text-destructive hover:text-destructive" onClick={removeSelected}>
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </Button>
            </div>
            {isAllocatable(single) && (
              <div className="rounded-md border bg-muted/40 p-2 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Users className="w-3 h-3" /> Allocation
                </div>
                {single.shape === "chair" && (
                  <div className="text-[11px] text-muted-foreground">
                    Seat ID: <b className="text-foreground">{seatCodeFor(single, scene) ?? "—"}</b>
                    <span className="ml-2 opacity-70">(auto-numbered)</span>
                  </div>
                )}
                <Field label={single.kind === "room" ? "Assigned to (occupant / cabin holder)" : "Assigned to (employee name)"}>
                  <Input
                    value={single.assignee ?? ""}
                    placeholder="e.g. Priya Sharma"
                    className="h-8"
                    onChange={(e) => updateItem(single.id, { assignee: e.target.value || undefined })}
                  />
                </Field>
                <Field label="Team / department">
                  <select
                    value={single.teamId ?? ""}
                    onChange={(e) => updateItem(single.id, { teamId: e.target.value || undefined })}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                  >
                    <option value="">— Unassigned —</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </Field>
                {single.shape === "chair" && (
                  <Field label="Override seat ID (optional)">
                    <Input
                      value={single.seatCode ?? ""}
                      placeholder={seatCodeFor({ ...single, seatCode: undefined }, scene) ?? "WS-01"}
                      className="h-8"
                      onChange={(e) => updateItem(single.id, { seatCode: e.target.value || undefined })}
                    />
                  </Field>
                )}
              </div>
            )}
            {(isWorkstationShape(single.shape) || single.groupId) && (
              <div className="rounded-md border bg-muted/40 p-2 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {isWorkstationShape(single.shape) ? "Workstation group" : "Component group"}
                </div>
                <div className="flex flex-wrap gap-2">
                  {isWorkstationShape(single.shape) && !single.groupId && (
                    <Button size="sm" variant="outline" onClick={() => explodeItem(single.id)}>
                      Explode to parts
                    </Button>
                  )}
                  {single.groupId && (
                    <Button size="sm" variant="outline" onClick={ungroupSelected}>
                      Ungroup
                    </Button>
                  )}
                  {single.kind === "room" && single.groupId && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => {
                          const r = saveClusterPreset(single.id);
                          if (r) toast.success(`Saved preset — ${r.count} components. Future "${single.label}" drops use this layout.`);
                        }}
                      >
                        <Save className="w-3.5 h-3.5 mr-1" /> Save as cluster preset
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { clearClusterPreset(single.type); toast.success("Reverted to default preset."); }}>
                        Reset preset
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {isWorkstationShape(single.shape)
                    ? "Chairs edit individually. Hold Shift while dragging a chair near a seat slot to snap it back. Press Shift+X to explode."
                    : single.kind === "room"
                    ? "Double-click the room to open the cluster editor. Save as preset stores this layout so future drops of this room start with the same components."
                    : "Ungroup to edit each part (desk, chair, storage…) independently — move, rotate, resize, or delete them on their own."}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Field label="Width (cm)">
                <Input
                  type="number"
                  value={Math.round(single.w)}
                  onChange={(e) => updateItem(single.id, { w: Math.max(10, Number(e.target.value) || 0) })}
                  className="h-8"
                />
              </Field>
              <Field label="Height (cm)">
                <Input
                  type="number"
                  value={Math.round(single.h)}
                  onChange={(e) => updateItem(single.id, { h: Math.max(10, Number(e.target.value) || 0) })}
                  className="h-8"
                />
              </Field>
              <Field label="X (cm)">
                <Input
                  type="number"
                  value={Math.round(single.x)}
                  onChange={(e) => updateItem(single.id, { x: Number(e.target.value) || 0 })}
                  className="h-8"
                />
              </Field>
              <Field label="Y (cm)">
                <Input
                  type="number"
                  value={Math.round(single.y)}
                  onChange={(e) => updateItem(single.id, { y: Number(e.target.value) || 0 })}
                  className="h-8"
                />
              </Field>
            </div>
            <Field label={`Rotation: ${Math.round(single.rotation)}°`}>
              <Slider
                value={[single.rotation]}
                min={0}
                max={360}
                step={1}
                onValueChange={(v) => updateItem(single.id, { rotation: v[0] })}
              />
            </Field>
            {single.kind === "room" && (
              <Field label="Fill color">
                <input
                  type="color"
                  value={oklchToHexFallback(single.color)}
                  onChange={(e) => updateItem(single.id, { color: hexToRgba(e.target.value, 0.45) })}
                  className="h-8 w-full rounded border border-border bg-background"
                />
              </Field>
            )}
            {single.shape === "door" && (
              <Field label="Swing arc">
                <div className="flex items-center justify-between rounded-md border border-border p-2 bg-background/40">
                  <span className="text-xs text-muted-foreground">Draw arc on opposite side</span>
                  <Switch
                    checked={!!single.flipArc}
                    onCheckedChange={(v) => updateItem(single.id, { flipArc: v })}
                  />
                </div>
              </Field>
            )}
            {single.shape === "svg" && single.svg && (
              <div className="rounded-md border border-border p-3 space-y-2 bg-background/40">
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  <Palette className="w-3.5 h-3.5" /> SVG palette
                </div>
                {single.svg.palette.length === 0 && (
                  <div className="text-[11px] text-muted-foreground">No editable colors found in this SVG.</div>
                )}
                <div className="space-y-1.5">
                  {single.svg.palette.map((orig) => {
                    const ov = single.svg!.overrides?.[orig] ?? orig;
                    return (
                      <div key={orig} className="flex items-center gap-2">
                        <input
                          type="color"
                          value={toHex(ov)}
                          onChange={(e) =>
                            updateItem(single.id, {
                              svg: {
                                ...single.svg!,
                                overrides: { ...(single.svg!.overrides ?? {}), [orig]: e.target.value },
                              },
                            })
                          }
                          className="h-7 w-10 rounded border border-border bg-background"
                        />
                        <span className="text-[11px] font-mono text-muted-foreground flex-1 truncate">{orig}</span>
                        {single.svg!.overrides?.[orig] && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-1.5 text-[10px]"
                            onClick={() => {
                              const next = { ...(single.svg!.overrides ?? {}) };
                              delete next[orig];
                              updateItem(single.id, { svg: { ...single.svg!, overrides: next } });
                            }}
                          >
                            reset
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {single.svg.palette.length > 0 && (
                  <div className="flex gap-1.5">
                    <input
                      type="color"
                      defaultValue="#3b82f6"
                      onChange={(e) => {
                        const v = e.target.value;
                        const overrides: Record<string, string> = {};
                        single.svg!.palette.forEach((p) => (overrides[p] = v));
                        updateItem(single.id, { svg: { ...single.svg!, overrides } });
                      }}
                      className="h-7 w-10 rounded border border-border bg-background"
                      title="Recolor all"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-7 text-xs"
                      onClick={() => updateItem(single.id, { svg: { ...single.svg!, overrides: {} } })}
                    >
                      Reset all
                    </Button>
                  </div>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-full h-7 text-xs gap-1"
                  onClick={() => {
                    addLocalAsset({
                      name: single.label,
                      inner: single.svg!.inner,
                      viewBox: single.svg!.viewBox,
                      width: single.w,
                      height: single.h,
                      palette: single.svg!.palette,
                    });
                    toast.success("Saved to your SVG library");
                  }}
                >
                  <Save className="w-3 h-3" /> Save to library
                </Button>
              </div>
            )}
            {single.shape === "conference" && (
              <Field label="Seats">
                <Input
                  type="number"
                  value={single.seats ?? 6}
                  min={2}
                  max={20}
                  onChange={(e) => updateItem(single.id, { seats: Math.max(2, Math.min(20, Number(e.target.value) || 2)) })}
                  className="h-8"
                />
              </Field>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => updateItem(single.id, { rotation: (single.rotation + 90) % 360 })}
              >
                Rotate 90°
              </Button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function oklchToHexFallback(color?: string) {
  if (!color) return "#cbd5e1";
  if (color.startsWith("#")) return color.slice(0, 7);
  return "#cbd5e1";
}

function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function toHex(c: string): string {
  if (!c) return "#888888";
  const s = c.trim().toLowerCase();
  if (s.startsWith("#")) return s.length === 4
    ? `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`
    : s.slice(0, 7);
  const m = s.match(/^rgba?\(([^)]+)\)/);
  if (m) {
    const [r, g, b] = m[1].split(",").map((v) => Math.max(0, Math.min(255, Math.round(parseFloat(v)))));
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
  }
  // named/oklch/other — leave as a neutral swatch
  return "#888888";
}